/** Assign one received hardware unit to a work order on the same case, including an approved past visit (#434). */
import { Button, Dialog, toast } from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Wrench } from 'lucide-react'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import { formatWhen } from '../../lib/display-time'
import { officeDateTimeLocal } from '../../lib/toronto-time'
import { useMe } from '../auth/session'
import { type HardwareUnitOut, procurementApi, procurementKeys, type WorkOrderChoice } from './api'

/** Calendar day in America/Toronto, `YYYY-MM-DD`, or null when the timestamp is missing or invalid. */
export function torontoDay(iso: string | null | undefined): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return officeDateTimeLocal(date.toISOString()).slice(0, 10)
}

export function visitDay(wo: WorkOrderChoice): string | null {
  return torontoDay(wo.checked_in_at) ?? torontoDay(wo.scheduled_start) ?? torontoDay(wo.approved_at)
}

/**
 * Visits on or after the receipt date, soonest first; other dated visits, most recent first; undated last.
 * The first following visit is the one after-the-fact paperwork usually means.
 */
export function rankInstallWorkOrders(items: WorkOrderChoice[], receivedAt: string | null | undefined): WorkOrderChoice[] {
  const received = torontoDay(receivedAt)
  return items
    .map((wo, index) => {
      const day = visitDay(wo)
      const follows = Boolean(day && received && day >= received)
      const score = day ? (follows ? 0 : 1) : 2
      return { wo, index, score, day: day ?? '' }
    })
    .sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score
      if (a.score === 0 && a.day !== b.day) return a.day.localeCompare(b.day)
      if (a.score === 1 && a.day !== b.day) return b.day.localeCompare(a.day)
      return a.index - b.index
    })
    .map((row) => row.wo)
}

export function suggestedWorkOrderId(items: WorkOrderChoice[], receivedAt: string | null | undefined): string | null {
  const ranked = rankInstallWorkOrders(items, receivedAt)
  const first = ranked[0]
  if (!first) return null
  const received = torontoDay(receivedAt)
  const day = visitDay(first)
  return received && day && day >= received ? first.id : null
}

function choiceLabel(wo: WorkOrderChoice, suggested: boolean): string {
  const day = visitDay(wo)
  const status = wo.status.replaceAll('_', ' ')
  const visit = day ? ` · visit ${formatWhen(`${day}T12:00:00`, { style: 'date' })}` : ''
  const hint = suggested ? ' · suggested' : ''
  return `${wo.display_number ?? 'Work order'} · ${status}${visit}${hint}`
}

export function InstallUnit({ unit, caseId, orderId }: { unit: HardwareUnitOut; caseId: string; orderId: string }) {
  const me = useMe()
  const qc = useQueryClient()
  const [open, setOpen] = React.useState(false)
  const [picked, setPicked] = React.useState<string | null>(null)
  const workOrders = useQuery({
    queryKey: procurementKeys.workOrders(caseId),
    queryFn: () => procurementApi.openWorkOrders(caseId),
    enabled: open,
    retry: 1,
  })
  const items = workOrders.data?.items ?? []
  const ranked = React.useMemo(() => rankInstallWorkOrders(items, unit.received_at), [items, unit.received_at])
  const suggestedId = suggestedWorkOrderId(items, unit.received_at)
  React.useEffect(() => {
    if (open && picked === null && ranked[0]) setPicked(ranked[0].id)
  }, [open, picked, ranked])
  const workOrderId = ranked.length === 1 ? (ranked[0]?.id ?? null) : ranked.some((wo) => wo.id === picked) ? picked : null
  const selected = ranked.find((wo) => wo.id === workOrderId)

  function setDialog(next: boolean) {
    setOpen(next)
    if (!next) setPicked(null)
  }

  const install = useMutation({
    mutationFn: () => {
      if (!workOrderId) throw new Error('Choose the work order where this unit was installed.')
      return procurementApi.assignUnit(unit.id, workOrderId)
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: procurementKeys.case(caseId) }),
        qc.invalidateQueries({ queryKey: procurementKeys.order(orderId) }),
        qc.invalidateQueries({ queryKey: ['case', caseId] }),
        qc.invalidateQueries({ queryKey: ['work-order', workOrderId] }),
        qc.invalidateQueries({ queryKey: ['proposals'] }),
        qc.invalidateQueries({ queryKey: ['billing'] }),
        qc.invalidateQueries({ queryKey: ['ledger'] }),
      ])
      toast.success(`Installed on ${selected?.display_number ?? 'the work order'}`)
      setDialog(false)
    },
  })

  if (unit.installed_wo_id) return <span className="text-success">installed on work order</span>
  if (!me || me.role === 'billing') return <span className="text-muted-foreground">not installed</span>

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setDialog(true)}>
        <Wrench /> Install on WO
      </Button>
      <Dialog
        open={open}
        onOpenChange={setDialog}
        title="Install received unit"
        description={[unit.model, unit.serial].filter(Boolean).join(' · ') || 'Received hardware unit'}
      >
        {workOrders.isPending ? <p className="text-sm text-muted-foreground">Finding work orders…</p> : null}
        {workOrders.isError ? (
          <p role="alert" className="rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
            Work orders are unreachable: {describeError(workOrders.error)} Nothing was changed.
          </p>
        ) : null}
        {workOrders.isSuccess && ranked.length === 0 ? (
          <p className="text-sm text-warning">This case has no work order to install on.</p>
        ) : null}
        {ranked.length > 1 ? (
          <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">Which work order?</legend>
            {ranked.map((wo) => (
              <Button
                key={wo.id}
                type="button"
                variant={picked === wo.id ? 'primary' : 'outline'}
                aria-pressed={picked === wo.id}
                className="justify-start"
                onClick={() => setPicked(wo.id)}
              >
                {choiceLabel(wo, wo.id === suggestedId)}
              </Button>
            ))}
          </fieldset>
        ) : null}
        {ranked.length === 1 ? (
          <p className="text-sm text-muted-foreground">Install on {choiceLabel(ranked[0] as WorkOrderChoice, false)}.</p>
        ) : null}
        {selected?.status === 'approved' ? (
          <p className="text-sm text-muted-foreground">
            This visit is already approved. Recording the install does not send a packet or notify the contractor.
          </p>
        ) : null}
        {install.isError ? (
          <p role="alert" className="rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
            Unit was not installed: {describeError(install.error)}
          </p>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setDialog(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!workOrderId} pending={install.isPending} onClick={() => install.mutate()}>
            Install on {selected?.display_number ?? 'work order'}
          </Button>
        </div>
      </Dialog>
    </>
  )
}
